import React, { createContext, useContext, useMemo } from 'react';
import {
  ChatUnreadCountMap,
  useChatUnreadCount,
} from '@/screens/chats/hooks/use-chat-unread-count';
import { useAuthenticatedUserContext } from './user-context';

export interface ChatsContextType {
  chatUnreadCountMap: ChatUnreadCountMap;
  hasUnreadChats: boolean;
  markChatViewed: (
    chatUUID: string,
    lastMessageTimestamp: string,
  ) => Promise<void>;
}

export const ChatsContext = createContext<ChatsContextType>({
  chatUnreadCountMap: {},
  hasUnreadChats: false,
  markChatViewed: async () => {},
});

interface Props {
  children?: React.ReactNode;
}

export const ChatsContextProvider: React.FC<Props> = ({ children }) => {
  const { chatUnreadCountMap, markChatViewed } = useChatUnreadCount();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();

  const value = useMemo(() => {
    const chatUnreadCount =
      chatUnreadCountMap[selectedRestaurantRelationship.chatUUIDs[0]] ?? 0;
    return {
      chatUnreadCountMap,
      hasUnreadChats: chatUnreadCount > 0,
      markChatViewed,
    };
  }, [chatUnreadCountMap, markChatViewed, selectedRestaurantRelationship]);

  return (
    <ChatsContext.Provider value={value}>{children}</ChatsContext.Provider>
  );
};

export const useChatsContext = () => useContext(ChatsContext);
